import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Palette,
  Type,
  Image,
  Save,
  RotateCcw,
  Eye,
  Check
} from 'lucide-react';

const COLOR_PRESETS = [
  { name: 'Default Red', primary: '#E31B23', secondary: '#1a1a1a', accent: '#C0C0C0' },
  { name: 'Ocean Blue', primary: '#0077B6', secondary: '#023E8A', accent: '#48CAE4' },
  { name: 'Forest Green', primary: '#2D6A4F', secondary: '#1B4332', accent: '#95D5B2' },
  { name: 'Royal Purple', primary: '#7B2CBF', secondary: '#240046', accent: '#C77DFF' },
  { name: 'Sunset Orange', primary: '#F4A261', secondary: '#264653', accent: '#E9C46A' },
  { name: 'Midnight', primary: '#6366F1', secondary: '#0F0F1A', accent: '#818CF8' },
];

export default function BrandingPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState({
    studio_name: '',
    tagline: '',
    primary_color: '#E31B23',
    secondary_color: '#1a1a1a',
    accent_color: '#C0C0C0',
    background_style: 'dark',
    welcome_message: '',
    footer_text: ''
  });
  const [showPreview, setShowPreview] = useState(false);

  const isTrainer = user?.role === 'trainer' || user?.role === 'admin';

  const fetchBranding = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranding(response.data);
    } catch (error) {
      console.error('Error fetching branding:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const handleSave = async () => {
    if (!isTrainer) {
      toast.error('Only trainers can update branding');
      return;
    }

    setSaving(true);
    try {
      const params = new URLSearchParams();
      if (branding.studio_name) params.append('studio_name', branding.studio_name);
      if (branding.tagline) params.append('tagline', branding.tagline);
      if (branding.primary_color) params.append('primary_color', branding.primary_color);
      if (branding.secondary_color) params.append('secondary_color', branding.secondary_color);
      if (branding.accent_color) params.append('accent_color', branding.accent_color);
      if (branding.background_style) params.append('background_style', branding.background_style);
      if (branding.welcome_message) params.append('welcome_message', branding.welcome_message);
      if (branding.footer_text) params.append('footer_text', branding.footer_text);

      await axios.put(`${API}/branding?${params}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Branding updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset branding to defaults? This cannot be undone.')) return;

    try {
      await axios.post(`${API}/branding/reset`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Branding reset to defaults');
      fetchBranding();
    } catch (error) {
      toast.error('Failed to reset branding');
    }
  };

  const applyPreset = (preset) => {
    setBranding(prev => ({
      ...prev,
      primary_color: preset.primary,
      secondary_color: preset.secondary,
      accent_color: preset.accent
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isTrainer) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Palette className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl text-white mb-2">Trainer Access Required</h2>
          <p className="text-gray-400">Branding settings are only available to trainers.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="branding-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            CUSTOM <span className="text-primary">BRANDING</span>
          </h1>
          <p className="text-gray-400 mt-1">Customize your studio's appearance</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            className="border-gray-600"
          >
            <Eye className="w-4 h-4 mr-2" />
            {showPreview ? 'Hide Preview' : 'Preview'}
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-red-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Preview Card */}
      {showPreview && (
        <div 
          className="p-6 rounded-sm border"
          style={{ 
            backgroundColor: branding.secondary_color,
            borderColor: branding.primary_color + '50'
          }}
        >
          <h3 
            className="text-2xl font-bold mb-2"
            style={{ color: branding.primary_color }}
          >
            {branding.studio_name || 'Your Studio Name'}
          </h3>
          <p style={{ color: branding.accent_color }}>
            {branding.tagline || 'Your tagline here'}
          </p>
          <p className="text-white mt-4">{branding.welcome_message || 'Welcome message preview'}</p>
          <div className="mt-4 flex gap-2">
            <button 
              className="px-4 py-2 rounded-sm text-white"
              style={{ backgroundColor: branding.primary_color }}
            >
              Primary Button
            </button>
            <button 
              className="px-4 py-2 rounded-sm border"
              style={{ borderColor: branding.accent_color, color: branding.accent_color }}
            >
              Secondary Button
            </button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Studio Info */}
        <div className="card-tactical">
          <h3 className="font-heading text-lg text-white uppercase mb-4 flex items-center gap-2">
            <Type className="w-5 h-5 text-primary" />
            Studio Information
          </h3>
          
          <div className="space-y-4">
            <div>
              <Label className="text-white">Studio Name</Label>
              <Input
                value={branding.studio_name}
                onChange={(e) => setBranding(prev => ({ ...prev, studio_name: e.target.value }))}
                placeholder="Bodies By Akeem"
                className="bg-secondary border-transparent mt-1"
              />
            </div>
            
            <div>
              <Label className="text-white">Tagline</Label>
              <Input
                value={branding.tagline}
                onChange={(e) => setBranding(prev => ({ ...prev, tagline: e.target.value }))}
                placeholder="Transform Your Body, Transform Your Life"
                className="bg-secondary border-transparent mt-1"
              />
            </div>
            
            <div>
              <Label className="text-white">Welcome Message</Label>
              <textarea
                value={branding.welcome_message}
                onChange={(e) => setBranding(prev => ({ ...prev, welcome_message: e.target.value }))}
                placeholder="Welcome to your fitness journey!"
                className="w-full mt-1 bg-secondary text-white rounded-sm p-2 border-transparent min-h-[80px]"
              />
            </div>
            
            <div>
              <Label className="text-white">Footer Text</Label>
              <Input
                value={branding.footer_text}
                onChange={(e) => setBranding(prev => ({ ...prev, footer_text: e.target.value }))}
                placeholder="© 2026 Your Studio. All rights reserved."
                className="bg-secondary border-transparent mt-1"
              />
            </div>
          </div>
        </div>

        {/* Colors */}
        <div className="card-tactical">
          <h3 className="font-heading text-lg text-white uppercase mb-4 flex items-center gap-2">
            <Palette className="w-5 h-5 text-primary" />
            Color Scheme
          </h3>
          
          <div className="space-y-4">
            <div>
              <Label className="text-white">Primary Color</Label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="color"
                  value={branding.primary_color}
                  onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={branding.primary_color}
                  onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                  placeholder="#E31B23"
                  className="bg-secondary border-transparent flex-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-white">Secondary Color</Label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="color"
                  value={branding.secondary_color}
                  onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={branding.secondary_color}
                  onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                  placeholder="#1a1a1a"
                  className="bg-secondary border-transparent flex-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-white">Accent Color</Label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="color"
                  value={branding.accent_color}
                  onChange={(e) => setBranding(prev => ({ ...prev, accent_color: e.target.value }))}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={branding.accent_color}
                  onChange={(e) => setBranding(prev => ({ ...prev, accent_color: e.target.value }))}
                  placeholder="#C0C0C0"
                  className="bg-secondary border-transparent flex-1"
                />
              </div>
            </div>

            {/* Color Presets */}
            <div className="pt-4 border-t border-border">
              <Label className="text-white mb-2 block">Quick Presets</Label>
              <div className="grid grid-cols-3 gap-2">
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => applyPreset(preset)}
                    className="p-2 rounded-sm bg-secondary/50 hover:bg-secondary transition-colors text-left"
                  >
                    <div className="flex gap-1 mb-1">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.primary }} />
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.secondary }} />
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.accent }} />
                    </div>
                    <span className="text-xs text-gray-400">{preset.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Background Style */}
      <div className="card-tactical">
        <h3 className="font-heading text-lg text-white uppercase mb-4">Background Style</h3>
        <div className="grid grid-cols-3 gap-4">
          {['dark', 'light', 'gradient'].map((style) => (
            <button
              key={style}
              onClick={() => setBranding(prev => ({ ...prev, background_style: style }))}
              className={`p-4 rounded-sm border transition-all ${
                branding.background_style === style
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-secondary/30 hover:border-gray-500'
              }`}
            >
              <div className={`h-16 rounded mb-2 ${
                style === 'dark' ? 'bg-gray-900' :
                style === 'light' ? 'bg-gray-100' :
                'bg-gradient-to-br from-gray-800 to-gray-900'
              }`} />
              <span className="text-white capitalize flex items-center gap-2">
                {branding.background_style === style && <Check className="w-4 h-4 text-primary" />}
                {style}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
